import { FC } from 'react';
import { SCUICollapsiblePaper } from '../../../atomic/molecules';
import { Box, Chip, Divider, IconButton, Typography, alpha } from '@mui/material';
import Swal from 'sweetalert2';
import CloseIcon from '@mui/icons-material/Close';
import StorageIcon from '@mui/icons-material/Storage';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import BarChartIcon from '@mui/icons-material/BarChart';
import DataObjectIcon from '@mui/icons-material/DataObject';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NumbersIcon from '@mui/icons-material/Numbers';
import { useTranslation } from 'react-i18next';
import './ProductDetailSerachTable.scss';
import { DSD } from '../detailStockSearch.classes';

const TYPE_META: Record<
    string,
    { icon: React.ReactNode; color: string; bgColor: string; borderColor: string; label: string }
> = {
    productWarehouse: {
        icon: <WarehouseIcon sx={{ fontSize: 16 }} />,
        color: '#2e7d32',
        bgColor: '#e8f5e9',
        borderColor: '#a5d6a7',
        label: 'Warehouse',
    },
    inventory: {
        icon: <InventoryIcon sx={{ fontSize: 16 }} />,
        color: '#1565c0',
        bgColor: '#e3f2fd',
        borderColor: '#90caf9',
        label: 'Inventory',
    },
    productMapper: {
        icon: <AccountTreeIcon sx={{ fontSize: 16 }} />,
        color: '#6a1b9a',
        bgColor: '#f3e5f5',
        borderColor: '#ce93d8',
        label: 'Product Mapper',
    },
    stockView: {
        icon: <BarChartIcon sx={{ fontSize: 16 }} />,
        color: '#e65100',
        bgColor: '#fff3e0',
        borderColor: '#ffcc80',
        label: 'Stock View',
    },
};

const PRIMITIVE_FIELD_ICONS: Record<string, React.ReactNode> = {
    productId: <NumbersIcon sx={{ fontSize: 13 }} />,
    optionId: <NumbersIcon sx={{ fontSize: 13 }} />,
    warehouseId: <NumbersIcon sx={{ fontSize: 13 }} />,
    totalQuantity: <StorageIcon sx={{ fontSize: 13 }} />,
    totalReservedQuantity: <StorageIcon sx={{ fontSize: 13 }} />,
    lastSyncTime: <AccessTimeIcon sx={{ fontSize: 13 }} />,
    version: <DataObjectIcon sx={{ fontSize: 13 }} />,
};

function formatFieldLabel(key: string): string {
    return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (s) => s.toUpperCase())
        .trim();
}

function formatFieldValue(key: string, value: any): string {
    if (key.toLowerCase().includes('time') || key.toLowerCase().includes('date')) {
        try {
            return new Date(value).toLocaleString('tr-TR');
        } catch {
            return String(value);
        }
    }
    return String(value);
}

export const SCUIDetailStockSearchDrawerContent: FC<{
    onClose?: any;
    datas?: any;
}> = ({ onClose, datas }) => {
    const { t } = useTranslation();

    function handleCopy() {
        let copyText = document.getElementById('productInfo')?.innerText;
        if (copyText) {
            navigator.clipboard.writeText(copyText);
        }
        Swal.fire({
            icon: 'success',
            title: t('general.copied'),
            showConfirmButton: false,
            timer: 1500,
        });
    }

    const typeKey = datas?.type as string | undefined;
    const typeMeta = typeKey ? (TYPE_META[typeKey] ?? null) : null;

    const primitiveFields = datas
        ? Object.entries(datas).filter(
              ([, v]) => v !== null && v !== undefined && typeof v !== 'object' && typeof v !== 'boolean'
          )
        : [];

    return (
        <Box className={DSD.root} sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fafbff' }}>
            {/* ── Header ── */}
            <Box
                className={DSD.header}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3,
                    py: 2.25,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    background: 'linear-gradient(135deg, #f8faff 0%, #eef2ff 100%)',
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2.5,
                            bgcolor: '#e8eeff',
                            border: '1.5px solid #c7d2fe',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 8px rgba(99,102,241,0.15)',
                        }}
                    >
                        <StorageIcon sx={{ fontSize: 22, color: '#4f46e5' }} />
                    </Box>
                    <Box>
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 700, fontSize: '1rem', color: 'text.primary', lineHeight: 1.25 }}
                        >
                            Stok Bilgisi
                        </Typography>
                        {typeMeta ? (
                            <Chip
                                size="small"
                                icon={
                                    <Box sx={{ '& svg': { color: `${typeMeta.color} !important` } }}>
                                        {typeMeta.icon}
                                    </Box>
                                }
                                label={typeMeta.label}
                                sx={{
                                    mt: 0.4,
                                    height: 20,
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    bgcolor: typeMeta.bgColor,
                                    color: typeMeta.color,
                                    border: `1px solid ${typeMeta.borderColor}`,
                                    '& .MuiChip-icon': { ml: '6px' },
                                }}
                            />
                        ) : (
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
                                Entity detay görünümü
                            </Typography>
                        )}
                    </Box>
                </Box>

                <IconButton
                    className={DSD.inputIconClose}
                    onClick={onClose}
                    size="small"
                    sx={{
                        color: 'text.secondary',
                        bgcolor: 'grey.100',
                        border: '1px solid',
                        borderColor: 'divider',
                        transition: 'all 0.25s ease',
                        '&:hover': {
                            bgcolor: '#fee2e2',
                            borderColor: '#fca5a5',
                            color: '#dc2626',
                            transform: 'rotate(90deg)',
                            boxShadow: '0 2px 8px rgba(220,38,38,0.2)',
                        },
                    }}
                >
                    <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>

            {/* ── Content ── */}
            <Box className={DSD.content} sx={{ flex: 1, overflowY: 'auto', p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {datas && (
                    <>
                        {/* Summary field chips */}
                        {primitiveFields.length > 0 && (
                            <Box
                                className={DSD.summaryPanel}
                                sx={{
                                    borderRadius: 2.5,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'white',
                                    boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
                                    overflow: 'hidden',
                                }}
                            >
                                <Box
                                    sx={{
                                        px: 2,
                                        py: 1.25,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <DataObjectIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.07em',
                                            fontSize: '0.65rem',
                                            color: 'text.disabled',
                                        }}
                                    >
                                        Özet Bilgiler
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                                        gap: 0,
                                    }}
                                >
                                    {primitiveFields.map(([key, value]) => (
                                        <Box
                                            key={key}
                                            sx={{
                                                px: 2,
                                                py: 1.25,
                                                borderRight: '1px solid',
                                                borderBottom: '1px solid',
                                                borderColor: 'divider',
                                                transition: 'background-color 0.15s ease',
                                                '&:hover': { bgcolor: alpha('#6366f1', 0.03) },
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: 'text.disabled',
                                                    fontSize: '0.62rem',
                                                    fontWeight: 700,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.06em',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.4,
                                                    mb: 0.4,
                                                }}
                                            >
                                                {PRIMITIVE_FIELD_ICONS[key] ?? <DataObjectIcon sx={{ fontSize: 11 }} />}
                                                {formatFieldLabel(key)}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: '0.8rem',
                                                    color: 'text.primary',
                                                    fontFamily:
                                                        typeof value === 'number' || key.toLowerCase().includes('id')
                                                            ? 'monospace'
                                                            : 'inherit',
                                                    wordBreak: 'break-all',
                                                }}
                                            >
                                                {formatFieldValue(key, value)}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        <Divider sx={{ my: 0 }} />

                        {/* JSON viewer */}
                        <SCUICollapsiblePaper
                            iconTooltip={t('general.copyToClipboard')}
                            icon={'fi-rr-copy'}
                            iconOnClick={() => handleCopy()}
                            paperText={'Entity'}
                            open={false}
                            withoutMargin
                            changeColorWhenOpen
                        >
                            <pre id="productInfo" className={`jsonViewDiv ${DSD.jsonPre}`}>
                                {JSON.stringify(datas, null, 2)}
                            </pre>
                        </SCUICollapsiblePaper>
                    </>
                )}
            </Box>
        </Box>
    );
};
